import { AsyncSelect } from '@tempo-io/sui-select';
import { toast } from '@tempo-io/sui-sonner';
import debounce from 'lodash.debounce';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSObjectWithLabel } from 'react-select';
import type { ParentItemOption } from '@/components/common/parent-item-search/parentItemSearch';
import {
  fetchParentItemOptions,
  getValidParentItemTypeIds,
  PARENT_SEARCH_LIMIT,
} from '@/components/common/parent-item-search/parentItemSearch';
import { TruncatedLabelWithSecondary } from '@/components/common/TruncatedLabelWithSecondary';
import { ItemTypeIcon } from '@/components/ui/item-type/itemTypeIcon';
import { useItemTypeLevelsQuery } from '@/lib/queries/common/useItemTypeLevelsQuery';
import { useItemTypesQuery } from '@/lib/queries/common/useItemTypesQuery';

interface ParentItemAutocompleteProps {
  value: ParentItemOption | null;
  onChange: (item: ParentItemOption | null) => void;
  validParentLevel: number;
  loopId?: number;
  excludedItemIds?: number[];
  disabled?: boolean;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  isClearable?: boolean;
}

const DEBOUNCE_DELAY_MS = 300;
const MENU_PORTAL_Z_INDEX = 3800;
const MENU_PORTAL_STYLES = {
  menuPortal: (base: CSSObjectWithLabel) => ({
    ...base,
    zIndex: MENU_PORTAL_Z_INDEX,
    pointerEvents: 'auto' as const,
  }),
};

export const ParentItemAutocomplete = ({
  value,
  onChange,
  validParentLevel,
  loopId,
  excludedItemIds = [],
  disabled,
  onMenuOpen,
  onMenuClose,
  isClearable = false,
}: ParentItemAutocompleteProps) => {
  const { t } = useTranslation();
  const { data: itemTypes } = useItemTypesQuery();
  const { data: itemTypeLevels } = useItemTypeLevelsQuery();

  const validParentItemTypeIds = useMemo(
    () => getValidParentItemTypeIds(itemTypes ?? [], itemTypeLevels ?? [], validParentLevel),
    [itemTypes, itemTypeLevels, validParentLevel],
  );
  const cacheKey = useMemo(
    () => `${[...validParentItemTypeIds].sort((a, b) => a - b).join('-')}:${loopId ?? ''}`,
    [validParentItemTypeIds, loopId],
  );
  const excludedItemIdSet = useMemo(() => new Set(excludedItemIds), [excludedItemIds]);

  const fetchItems = useMemo(
    () =>
      debounce((inputValue: string, callback: (options: ParentItemOption[]) => void) => {
        fetchParentItemOptions({
          inputValue,
          validParentItemTypeIds,
          loopId,
          excludedItemIds: excludedItemIdSet,
          limit: PARENT_SEARCH_LIMIT,
        })
          .then(callback)
          .catch(() => {
            toast.error(t(($) => $.createItem.fields.parent.loadError));
            callback([]);
          });
      }, DEBOUNCE_DELAY_MS),
    [excludedItemIdSet, validParentItemTypeIds, loopId, t],
  );

  return (
    <AsyncSelect<ParentItemOption>
      key={cacheKey}
      height={40}
      cacheOptions
      defaultOptions={validParentItemTypeIds.length > 0}
      loadOptions={fetchItems}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      // @ts-expect-error sui-select omits `styles` from its public type
      styles={MENU_PORTAL_STYLES}
      value={value}
      getOptionLabel={(option) => option.name}
      getOptionValue={(option) => option.id.toString()}
      formatOptionLabel={(option) => {
        const itemType = itemTypes?.find((t) => t.id === option.itemTypeId);
        return (
          <div className="flex min-w-0 items-center gap-2">
            {itemType && <ItemTypeIcon itemType={itemType} className="h-4 w-4 shrink-0" />}
            <TruncatedLabelWithSecondary
              label={option.name}
              secondaryLabel={option.externalKey}
              secondaryAlign="right"
              tooltipContentStyle={{ zIndex: MENU_PORTAL_Z_INDEX + 100 }}
            />
          </div>
        );
      }}
      onChange={onChange}
      isClearable={isClearable}
      placeholder={t(($) => $.createItem.fields.parent.placeholder)}
      isDisabled={disabled}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      noOptionsMessage={() => t(($) => $.createItem.fields.parent.emptyMessage)}
    />
  );
};
